import type { FC, SVGProps } from "react"
import { IconWebhookOutline18 as Webhook } from "nucleo-ui-outline-18"

import { McpIcon } from "@productlane/ui/src/icons/project/mcp-icon"

import {
	ChargebeeLogo,
	MintlifyLogo,
	MixpanelLogo,
	PaddleLogo,
	PostHogLogo,
	RevenueCatLogo,
	StripeTileLogo,
	SupabaseLogo,
} from "./brand-logos"

type Logo = FC<SVGProps<SVGSVGElement>>

// How the server URL is set: fixed by the vendor, prefilled but editable (a
// region), or built from one value the admin types (a project or site name).
export type PresetUrl =
	| { kind: "fixed"; url: string }
	| { kind: "editable"; url: string; hint: string }
	| {
			kind: "template"
			template: string
			label: string
			placeholder: string
			hint: string
	  }

// What the vendor wants as credentials. Every key ends up as a stored header.
export type PresetAuth =
	| { kind: "none" }
	| {
			kind: "bearer"
			label: string
			helpUrl: string
			hint: string
	  }
	| {
			kind: "basic-bearer"
			userLabel: string
			secretLabel: string
			helpUrl: string
			hint: string
	  }

export interface VendorPreset {
	id: string
	title: string
	summary: string
	Logo: Logo
	/** The mark is a full-bleed app tile, so it fills the logo box edge to edge. */
	logoFillsTile?: boolean
	url: PresetUrl
	auth: PresetAuth
	/** Hosts this vendor's servers live on, so a saved connector shows the logo. */
	hosts: readonly string[]
	/** A docs server answers anyone, so it starts as public knowledge. */
	publicKnowledge?: boolean
}

export type CustomPresetId = "api" | "mcp"

export interface CustomPreset {
	id: CustomPresetId
	title: string
	summary: string
	Logo: Logo
	type: "HTTP" | "MCP"
}

export const CUSTOM_PRESETS: readonly CustomPreset[] = [
	{
		id: "api",
		title: "Your own API",
		summary: "Show account data from your backend in threads.",
		Logo: Webhook,
		type: "HTTP",
	},
	{
		id: "mcp",
		title: "Any MCP server",
		summary: "Paste a server URL and pick what the agent can do.",
		Logo: McpIcon,
		type: "MCP",
	},
]

export const VENDOR_PRESETS: readonly VendorPreset[] = [
	{
		id: "stripe",
		title: "Stripe",
		summary: "Customers, invoices, subscriptions and refunds.",
		Logo: StripeTileLogo,
		logoFillsTile: true,
		url: { kind: "fixed", url: "https://mcp.stripe.com" },
		auth: {
			kind: "bearer",
			label: "Stripe API key",
			helpUrl: "https://dashboard.stripe.com/apikeys",
			hint: "Create a restricted key tagged for agents. Stripe stops accepting other keys on Nov 30, 2026.",
		},
		hosts: ["mcp.stripe.com"],
	},
	{
		id: "posthog",
		title: "PostHog",
		summary: "Events, people, feature flags and errors.",
		Logo: PostHogLogo,
		url: { kind: "fixed", url: "https://mcp.posthog.com/mcp" },
		auth: {
			kind: "bearer",
			label: "Personal API key",
			helpUrl: "https://app.posthog.com/settings/user-api-keys",
			hint: "Create a key with the MCP Server preset.",
		},
		hosts: ["mcp.posthog.com"],
	},
	{
		id: "docs",
		title: "Mintlify",
		summary: "Search docs hosted on Mintlify.",
		Logo: MintlifyLogo,
		url: {
			kind: "template",
			template: "https://{value}/mcp",
			label: "Docs domain",
			placeholder: "docs.example.com",
			hint: "The domain your docs live on.",
		},
		auth: { kind: "none" },
		hosts: [],
		publicKnowledge: true,
	},
	{
		id: "supabase",
		title: "Supabase",
		summary: "Users and rows in your database, read-only.",
		Logo: SupabaseLogo,
		url: {
			kind: "template",
			template:
				"https://mcp.supabase.com/mcp?project_ref={value}&read_only=true",
			label: "Project ref",
			placeholder: "abcdefghijklmnopqrst",
			hint: "Found in your project's settings.",
		},
		auth: {
			kind: "bearer",
			label: "Access token",
			helpUrl: "https://supabase.com/dashboard/account/tokens",
			hint: "A personal access token.",
		},
		hosts: ["mcp.supabase.com"],
	},
	{
		id: "paddle",
		title: "Paddle",
		summary: "Transactions, subscriptions and customers.",
		Logo: PaddleLogo,
		url: { kind: "fixed", url: "https://mcp.paddle.com/mcp" },
		auth: {
			kind: "bearer",
			label: "Paddle API key",
			helpUrl: "https://vendors.paddle.com/authentication-v2",
			hint: "A read-only key is enough for lookups.",
		},
		hosts: ["mcp.paddle.com", "sandbox-mcp.paddle.com"],
	},
	{
		id: "chargebee",
		title: "Chargebee",
		summary: "Subscriptions, invoices and customers.",
		Logo: ChargebeeLogo,
		url: {
			kind: "template",
			template: "https://{value}.mcp.chargebee.com/data_lookup_agent",
			label: "Site name",
			placeholder: "acme",
			hint: "The part before .chargebee.com.",
		},
		auth: {
			kind: "bearer",
			label: "MCP API key",
			helpUrl:
				"https://www.chargebee.com/docs/billing/2.0/ai-in-chargebee/custom-mcp-server",
			hint: "Created on the MCP server page in Chargebee.",
		},
		hosts: ["mcp.chargebee.com", "mcp.eu.chargebee.com"],
	},
	{
		id: "revenuecat",
		title: "RevenueCat",
		summary: "Subscribers, entitlements and purchases.",
		Logo: RevenueCatLogo,
		url: { kind: "fixed", url: "https://mcp.revenuecat.ai/mcp" },
		auth: {
			kind: "bearer",
			label: "Secret API key",
			helpUrl: "https://app.revenuecat.com/",
			hint: "An API v2 secret key. Read-only works.",
		},
		hosts: ["mcp.revenuecat.ai"],
	},
	{
		id: "mixpanel",
		title: "Mixpanel",
		summary: "What a user did in your product.",
		Logo: MixpanelLogo,
		url: {
			kind: "editable",
			url: "https://mcp.mixpanel.com/mcp",
			hint: "Use mcp-eu.mixpanel.com for EU projects.",
		},
		auth: {
			kind: "basic-bearer",
			userLabel: "Service account username",
			secretLabel: "Service account secret",
			helpUrl: "https://docs.mixpanel.com/docs/mcp",
			hint: "Create a service account in your project settings.",
		},
		hosts: ["mcp.mixpanel.com", "mcp-eu.mixpanel.com", "mcp-in.mixpanel.com"],
	},
]

export function findVendorPreset(id: string | undefined): VendorPreset | null {
	return VENDOR_PRESETS.find((preset) => preset.id === id) ?? null
}

export function findCustomPreset(id: string | undefined): CustomPreset | null {
	return CUSTOM_PRESETS.find((preset) => preset.id === id) ?? null
}

// A host matches its own entry and any subdomain of it (acme.mcp.chargebee.com).
export function vendorPresetForUrl(url: string): VendorPreset | null {
	let host: string
	try {
		host = new URL(url).host.toLowerCase()
	} catch {
		return null
	}
	return (
		VENDOR_PRESETS.find((preset) =>
			preset.hosts.some(
				(entry) => host === entry || host.endsWith(`.${entry}`),
			),
		) ?? null
	)
}

export function presetServerUrl(preset: VendorPreset, value: string): string {
	switch (preset.url.kind) {
		case "fixed":
			return preset.url.url
		case "editable":
			return value.trim() || preset.url.url
		case "template": {
			const cleaned = value
				.trim()
				.replace(/^https?:\/\//, "")
				.replace(/\/+$/, "")
			return preset.url.template.replace("{value}", encodeURI(cleaned))
		}
	}
}

// Mixpanel's service accounts sign in as "Bearer Basic <base64 user:secret>".
export function presetAuthHeader(
	preset: VendorPreset,
	values: { key: string; user: string; secret: string },
): { key: string; value: string } | null {
	switch (preset.auth.kind) {
		case "none":
			return null
		case "bearer":
			return { key: "Authorization", value: `Bearer ${values.key.trim()}` }
		case "basic-bearer": {
			const encoded = btoa(`${values.user.trim()}:${values.secret.trim()}`)
			return { key: "Authorization", value: `Bearer Basic ${encoded}` }
		}
	}
}
